import type { Dispatch, SetStateAction } from "react";

import { AlertSeverity, type Nullable, YesNo } from "@/types/utils";

export interface QuestionWarning {
  helpHref?: string;
  message: string;
  severity: AlertSeverity;
}

export interface ReviewPageScoringGuidelines {
  high: string;
  low: string;
  medium: string;
}

export enum QuestionType {
  Email = "email",
  FileUpload = "file_upload",
  MultipleChoice = "multiple_choice",
  Name = "name",
  Number = "numerical",
  Phone = "phone",
  ShortAnswer = "text",
  SingleChoice = "single_choice",
  /**
   * @deprecated Voice Phase 1. Use AssessmentQuestionResponseFormat instead (Voice Phase 2).
   * Retained for backward compatibility with existing data.
   */
  Voice = "voice",
  YesNo = "yes_no",
}

export type ChoiceBasicQuestionQuestionType =
  | QuestionType.SingleChoice
  | QuestionType.MultipleChoice;

export type SimpleBasicQuestionQuestionType =
  | QuestionType.Email
  | QuestionType.Name
  | QuestionType.Phone
  | QuestionType.ShortAnswer;

export enum NumericalRule {
  LessThan = "<",
  GreaterThan = ">",
  LessThanOrEqualTo = "<=",
  GreaterThanOrEqualTo = ">=",
  EqualTo = "=",
  NotEqualTo = "!=",
}

export enum ChoiceQualifyingExpression {
  Is = "[+]",
  IsNot = "[!]",
  Required = "[*]",
}

export interface Condition {
  optionText: string;
  qualifier?: ChoiceQualifyingExpression;
}

export interface ReviewPageQuestion {
  canSkip?: boolean;
  isSelected: boolean;
  question: string;
  questionId: string;
  recordingDurationSeconds?: number;
  responseFormat?: AssessmentQuestionResponseFormat;
}

export interface WarningBypassLog {
  timestamp: string;
  component: string[];
  warningType: string;
  userId: string;
  userEmail: string;
  warningMessage: string;
  resolvedAt?: Nullable<string>;
}

export enum AssessmentQuestionResponseFormat {
  Written = "written",
  VoiceRecommended = "voice_recommended",
  VoiceRequired = "voice_required",
}

export const RESPONSE_FORMAT_LABELS: Record<
  AssessmentQuestionResponseFormat,
  string
> = {
  [AssessmentQuestionResponseFormat.Written]: "Written",
  [AssessmentQuestionResponseFormat.VoiceRecommended]: "Voice recommended",
  [AssessmentQuestionResponseFormat.VoiceRequired]: "Voice required",
};

export interface ReviewPageCompetency {
  custom?: boolean;
  definition: string;
  displayName: string;
  hasCustomScoringGuidelines: boolean;
  id: string;
  isIncluded: boolean;
  name: string;
  questions: ReviewPageQuestion[];
  scoringGuidelines: ReviewPageScoringGuidelines;
  warningBypassLog?: Nullable<WarningBypassLog[]>;
}

// Avoid tons of indents on validationRules
// prettier-ignore
export interface ReviewPageBasicQuestionPayload<
  T extends QuestionType = QuestionType,
> {
  // Technically optional, but effectively required in virtually all cases. This
  // remains optional only for backwards compatability with old data.
  internalLabel?: string;
  isIncluded: boolean;
  isQualifying: boolean;
  questionText: string;
  questionType: T;
  showInSummary?: boolean;
  validationRules:Nullable<
      T extends ChoiceBasicQuestionQuestionType
    ? { choiceRules?: Condition[] }
    : T extends QuestionType.YesNo
    ? { yesNoRule?: YesNo }
    : T extends QuestionType.Number
    ? { numericalRule?: string }
    // Non-qualifying question types
    : never
  >;
}

export interface ReviewPageBasicQuestion<
  T extends QuestionType = QuestionType,
> extends ReviewPageBasicQuestionPayload<T> {
  id: string;
  isCustom: boolean;
  // rootId is undefined for identifying questions (name, email, phone). Those
  // questions are singular records in the database shared among all screeners.
  rootId?: string;
}

// Used for questions being edited or updated
export type ReviewPageDraftQuestion<T extends QuestionType = QuestionType> =
  | ReviewPageBasicQuestionPayload<T>
  | ReviewPageBasicQuestion<T>;

export type QuestionWithValidationRules = ReviewPageDraftQuestion<
  ChoiceBasicQuestionQuestionType | QuestionType.Number | QuestionType.YesNo
>;

export type QuestionStateSetter<T extends QuestionType = QuestionType> =
  Dispatch<SetStateAction<ReviewPageDraftQuestion<T>>>;
